<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="patron-detail-view">
      <!-- Back Button -->
      <IntercomButton variant="ghost" size="sm" @click="$router.push('/patrons')" class="back-button">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        {{ $t('common.back') }}
      </IntercomButton>

      <div v-if="isLoading" class="loading-state">
        {{ $t('common.loading') }}
      </div>

      <template v-else-if="patron">
        <!-- Patron Header -->
        <div class="patron-header">
          <div class="patron-info">
            <h1 class="patron-name">
              <span v-if="patron.patron_number" class="patron-number">{{ patron.patron_number }}</span>
              {{ patron.name }}
            </h1>
            <div class="patron-meta">
              <IntercomBadge v-if="patron.patron_type" :variant="getTypeVariant(patron.patron_type)">
                {{ $t(`patron.types.${patron.patron_type}`) }}
              </IntercomBadge>
              <span v-if="patron.phone" class="meta-item">{{ patron.phone }}</span>
              <span v-if="patron.email" class="meta-item">{{ patron.email }}</span>
            </div>
          </div>
          <div class="patron-actions">
            <IntercomButton variant="secondary" @click="showEditModal = true">
              {{ $t('common.edit') }}
            </IntercomButton>
            <IntercomButton variant="primary" @click="$router.push(`/donations?new=true&patron_id=${patron.id}`)">
              {{ $t('donation.addDonation') }}
            </IntercomButton>
          </div>
        </div>

        <!-- Stats Cards -->
        <div class="stats-row">
          <IntercomCard class="stat-card">
            <div class="stat-label">{{ $t('patron.totalDonated') }}</div>
            <div class="stat-value">{{ formatCompactCurrency(patron.total_donated || 0) }}</div>
          </IntercomCard>
          <IntercomCard class="stat-card">
            <div class="stat-label">{{ $t('patron.donationCount') }}</div>
            <div class="stat-value">{{ patron.donation_count || 0 }}</div>
          </IntercomCard>
          <IntercomCard class="stat-card">
            <div class="stat-label">{{ $t('patron.lastDonation') }}</div>
            <div class="stat-value">{{ patron.last_donation_date ? formatDate(patron.last_donation_date) : '-' }}</div>
          </IntercomCard>
        </div>

        <!-- Details Card -->
        <IntercomCard v-if="patron.address || patron.notes" class="details-card">
          <h3>Details</h3>
          <div v-if="patron.address" class="detail-row">
            <span class="detail-label">{{ $t('patron.address') }}</span>
            <span class="detail-value">{{ patron.address }}</span>
          </div>
          <div v-if="patron.notes" class="detail-row">
            <span class="detail-label">{{ $t('patron.notes') }}</span>
            <span class="detail-value">{{ patron.notes }}</span>
          </div>
        </IntercomCard>

        <!-- Recurring Commitments -->
        <IntercomCard class="commitments-card">
          <div class="section-header">
            <h3>{{ $t('commitment.title') }}</h3>
            <IntercomButton variant="secondary" size="sm" @click="showAddCommitmentModal = true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {{ $t('commitment.addCommitment') }}
            </IntercomButton>
          </div>
          <IntercomTable
            :columns="commitmentColumns"
            :data="commitments"
            :loading="loadingCommitments"
            hoverable
            clickable
            @row-click="handleCommitmentClick"
          >
            <template #cell-donation_type="{ value }">
              <IntercomBadge :variant="getDonationTypeVariant(value)">
                {{ value ? $t(`donation.types.${value}`) : '' }}
              </IntercomBadge>
            </template>
            <template #cell-frequency="{ value }">
              {{ value ? $t(`donation.frequencies.${value}`) : '' }}
            </template>
            <template #cell-amount="{ value }">
              {{ value ? formatCompactCurrency(value) : '-' }}
            </template>
            <template #cell-status="{ value }">
              <IntercomBadge :variant="value === 'active' ? 'success' : 'default'">
                {{ value }}
              </IntercomBadge>
            </template>
          </IntercomTable>
          <p v-if="!loadingCommitments && commitments.length === 0" class="empty-text">
            {{ $t('commitment.noCommitments') }}
          </p>
        </IntercomCard>

        <!-- Donation History -->
        <IntercomCard class="donations-card">
          <div class="section-header">
            <h3>{{ $t('donation.title') }}</h3>
          </div>
          <IntercomTable
            :columns="donationColumns"
            :data="donations"
            :loading="loadingDonations"
            hoverable
            clickable
            @row-click="handleDonationClick"
          >
            <template #cell-donation_type="{ value }">
              <IntercomBadge :variant="getDonationTypeVariant(value)">
                {{ value ? $t(`donation.types.${value}`) : '' }}
              </IntercomBadge>
            </template>
            <template #cell-amount="{ value }">
              {{ formatCompactCurrency(value || 0) }}
            </template>
            <template #cell-donation_date="{ value }">
              {{ formatDate(value) }}
            </template>
          </IntercomTable>
          <p v-if="!loadingDonations && donations.length === 0" class="empty-text">
            {{ $t('donation.noDonations') }}
          </p>
        </IntercomCard>
      </template>

      <!-- Add Commitment Modal -->
      <IntercomModal v-model:show="showAddCommitmentModal" :title="editingCommitment ? $t('commitment.editCommitment') : $t('commitment.addCommitment')">
        <form @submit.prevent="handleCommitmentSave" class="commitment-form">
          <div class="cf-field">
            <IntercomSelect
              v-model="commitmentForm.donation_type"
              :label="$t('donation.type')"
              :options="donationTypeOptions"
              required
            />
          </div>
          <div v-if="commitmentForm.donation_type !== 'volunteer'" class="cf-field">
            <IntercomInput
              v-model.number="commitmentForm.amount"
              :label="$t('donation.amount')"
              type="number"
              :placeholder="commitmentForm.donation_type === 'in_kind' ? $t('donation.estimatedValuePlaceholder') : '0.00'"
            />
          </div>
          <div class="cf-field">
            <IntercomInput
              v-model="commitmentForm.description"
              :label="$t('donation.description')"
              placeholder="e.g., Monthly rice donation"
            />
          </div>
          <div class="cf-field">
            <IntercomSelect
              v-model="commitmentForm.frequency"
              :label="$t('donation.frequency')"
              :options="frequencyOptions"
              required
            />
          </div>
          <div class="cf-field">
            <IntercomInput
              v-model.number="commitmentForm.recurring_day"
              :label="$t('donation.recurringDay')"
              type="number"
              :min="1"
              :max="28"
              required
            />
          </div>
          <div class="cf-field">
            <IntercomSelect
              v-model="commitmentForm.program_id"
              :label="$t('commitment.program')"
              :options="programOptions"
              :placeholder="$t('commitment.programOptional')"
            />
          </div>
          <div class="cf-field">
            <IntercomDateInput
              v-model="commitmentForm.start_date"
              :label="$t('commitment.startDate')"
              required
            />
          </div>
          <div class="cf-field">
            <IntercomDateInput
              v-model="commitmentForm.end_date"
              :label="$t('commitment.endDate')"
            />
          </div>
          <div class="cf-field cf-full">
            <IntercomInput
              v-model="commitmentForm.notes"
              :label="$t('donation.notes')"
              :placeholder="$t('common.optional')"
            />
          </div>
          <div class="cf-actions">
            <IntercomButton type="button" variant="ghost" @click="closeCommitmentModal" :disabled="isSavingCommitment">
              {{ $t('common.cancel') }}
            </IntercomButton>
            <IntercomButton type="submit" variant="primary" :loading="isSavingCommitment">
              {{ $t('common.save') }}
            </IntercomButton>
          </div>
        </form>
      </IntercomModal>

      <!-- Edit Modal -->
      <IntercomModal v-model:show="showEditModal" :title="$t('patron.editPatron')">
        <PatronForm
          :patron="patron"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="showEditModal = false"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomModal,
  IntercomSelect,
  IntercomInput,
  IntercomDateInput,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import PatronForm from '../components/PatronForm.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { getPatron, updatePatron, listDonations, listPatronCommitments, createCommitment, updateCommitment, listPrograms } = useSeva()
const { navSections } = useNavigation()

// State
const patron = ref(null)
const donations = ref([])
const commitments = ref([])
const programs = ref([])
const isLoading = ref(false)
const loadingDonations = ref(false)
const loadingCommitments = ref(false)
const isSaving = ref(false)
const isSavingCommitment = ref(false)
const showEditModal = ref(false)
const showAddCommitmentModal = ref(false)
const editingCommitment = ref(null)

const commitmentForm = reactive({
  donation_type: 'money',
  amount: null,
  description: '',
  frequency: 'monthly',
  recurring_day: dayjs().date() <= 28 ? dayjs().date() : 1,
  program_id: null,
  start_date: dayjs().format('YYYY-MM-DD'),
  end_date: '',
  notes: '',
})

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''

// Commitment options
const donationTypeOptions = computed(() => [
  { value: 'money', label: t('donation.types.money') },
  { value: 'in_kind', label: t('donation.types.in_kind') },
  { value: 'volunteer', label: t('donation.types.volunteer') },
])

const frequencyOptions = computed(() => [
  { value: 'monthly', label: t('donation.frequencies.monthly') },
  { value: 'quarterly', label: t('donation.frequencies.quarterly') },
  { value: 'yearly', label: t('donation.frequencies.yearly') },
])

const programOptions = computed(() => {
  const opts = programs.value.map(p => ({ value: p.id, label: p.name }))
  opts.unshift({ value: null, label: t('commitment.programOptional') })
  return opts
})

const commitmentColumns = [
  { key: 'donation_type', title: t('donation.type'), width: '110px' },
  { key: 'amount', title: t('donation.amount'), width: '100px', align: 'right' },
  { key: 'description', title: t('donation.description'), width: '160px' },
  { key: 'frequency', title: t('donation.frequency'), width: '110px' },
  { key: 'recurring_day', title: t('donation.recurringDay'), width: '90px' },
  { key: 'status', title: 'Status', width: '90px' },
]

const donationColumns = [
  { key: 'donation_number', title: t('donation.number'), width: '120px' },
  { key: 'donation_type', title: t('donation.type'), width: '120px' },
  { key: 'amount', title: t('donation.amount'), width: '120px', align: 'right' },
  { key: 'donation_date', title: t('donation.date'), width: '120px' },
]

const getTypeVariant = (type) => {
  switch (type) {
    case 'individual': return 'info'
    case 'corporate': return 'success'
    case 'foundation': return 'warning'
    default: return 'default'
  }
}

const getDonationTypeVariant = (type) => {
  switch (type) {
    case 'money': return 'success'
    case 'in_kind': return 'info'
    case 'volunteer': return 'warning'
    default: return 'default'
  }
}

const fetchPatronData = async () => {
  const patronId = route.params.id
  isLoading.value = true
  try {
    const response = await getPatron(patronId)
    patron.value = response?.patron || response
  } catch (error) {
    console.error('Failed to fetch patron:', error)
    toast.error(t('errors.loadFailed'))
  } finally {
    isLoading.value = false
  }
}

const fetchDonations = async () => {
  const patronId = route.params.id
  loadingDonations.value = true
  try {
    const response = await listDonations({ patron_id: patronId })
    donations.value = response?.data || []
  } catch (error) {
    console.error('Failed to fetch donations:', error)
  } finally {
    loadingDonations.value = false
  }
}

const fetchCommitments = async () => {
  const patronId = route.params.id
  loadingCommitments.value = true
  try {
    const response = await listPatronCommitments(patronId)
    commitments.value = response?.commitments || []
  } catch (error) {
    console.error('Failed to fetch commitments:', error)
  } finally {
    loadingCommitments.value = false
  }
}

const resetCommitmentForm = () => {
  commitmentForm.donation_type = 'money'
  commitmentForm.amount = null
  commitmentForm.description = ''
  commitmentForm.frequency = 'monthly'
  commitmentForm.recurring_day = dayjs().date() <= 28 ? dayjs().date() : 1
  commitmentForm.program_id = null
  commitmentForm.start_date = dayjs().format('YYYY-MM-DD')
  commitmentForm.end_date = ''
  commitmentForm.notes = ''
}

const closeCommitmentModal = () => {
  showAddCommitmentModal.value = false
  editingCommitment.value = null
  resetCommitmentForm()
}

const handleCommitmentClick = (commitment) => {
  editingCommitment.value = commitment
  commitmentForm.donation_type = commitment.donation_type || 'money'
  commitmentForm.amount = commitment.amount || null
  commitmentForm.description = commitment.description || ''
  commitmentForm.frequency = commitment.frequency || 'monthly'
  commitmentForm.recurring_day = commitment.recurring_day || 1
  commitmentForm.program_id = commitment.program_id || null
  commitmentForm.start_date = commitment.start_date
    ? dayjs(commitment.start_date).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD')
  commitmentForm.end_date = commitment.end_date
    ? dayjs(commitment.end_date).format('YYYY-MM-DD')
    : ''
  commitmentForm.notes = commitment.notes || ''
  showAddCommitmentModal.value = true
}

const handleCommitmentSave = async () => {
  const patronId = route.params.id
  isSavingCommitment.value = true
  try {
    const data = { ...commitmentForm }
    if (!data.end_date) data.end_date = null
    if (!data.notes) data.notes = null
    if (!data.program_id) data.program_id = null
    if (!data.amount) data.amount = null

    if (editingCommitment.value) {
      await updateCommitment(editingCommitment.value.id, data)
      toast.success(t('success.commitmentUpdated'))
    } else {
      await createCommitment(patronId, data)
      toast.success(t('success.commitmentCreated'))
    }
    closeCommitmentModal()
    fetchCommitments()
  } catch (error) {
    console.error('Failed to save commitment:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  } finally {
    isSavingCommitment.value = false
  }
}

const handleDonationClick = (donation) => {
  router.push(`/donations/${donation.id}`)
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    const data = { ...formData }
    if (data.phone && data.phone.startsWith('+91')) data.phone = data.phone.slice(3)
    if (!data.phone) data.phone = null

    await updatePatron(patron.value.id, data)
    toast.success(t('success.patronUpdated'))
    showEditModal.value = false
    fetchPatronData()
  } catch (error) {
    console.error('Failed to save patron:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(async () => {
  fetchBusiness()
  fetchPatronData()
  fetchDonations()
  fetchCommitments()
  try {
    const resp = await listPrograms({ limit: 100 })
    programs.value = resp?.data || []
  } catch {
    programs.value = []
  }
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.patron-detail-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.back-button {
  margin-bottom: var(--space-4);
}

.loading-state {
  text-align: center;
  padding: var(--space-12);
  color: var(--color-gray-500);
}

.patron-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-6);
}

.patron-name {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-2) 0;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.patron-number {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  background: var(--color-gray-100);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
}

.patron-meta {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
}

.meta-item {
  padding-left: var(--space-3);
  border-left: 1px solid var(--color-gray-300);
}

.patron-actions {
  display: flex;
  gap: var(--space-3);
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.stat-card {
  padding: var(--space-4);
  text-align: center;
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-bottom: var(--space-1);
}

.stat-value {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

.details-card {
  margin-bottom: var(--space-6);
}

.details-card h3 {
  margin: 0 0 var(--space-4) 0;
  font-size: var(--font-size-lg);
}

.detail-row {
  display: flex;
  gap: var(--space-4);
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--color-gray-100);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  width: 120px;
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
}

.detail-value {
  flex: 1;
  color: var(--color-charcoal);
}

.commitments-card {
  margin-bottom: var(--space-6);
}

.commitment-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.cf-field {
  display: flex;
  flex-direction: column;
}

.cf-full {
  grid-column: 1 / -1;
}

.cf-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-2);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}

.donations-card {
  margin-bottom: var(--space-6);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4);
}

.section-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {
  .patron-detail-view {
    padding: var(--space-4);
  }

  .patron-header {
    flex-direction: column;
    gap: var(--space-4);
  }

  .stats-row {
    grid-template-columns: 1fr;
  }
}
</style>
